import { useRef, useState } from 'react';

import { useApiPetCompatibilityCreate } from '@rover/rsdk/src/apiClient/latest/api-pet-compatibility-create';
import {
  type PetCompatibilityAnswerEnum as Answer,
  PetCompatibilityAnswerEnum,
} from '@rover/rsdk/src/apiClient/latest/model/petCompatibilityAnswerEnum';
import type { PetCompatibilityQuestionEnum as Question } from '@rover/rsdk/src/apiClient/latest/model/petCompatibilityQuestionEnum';
import type { PetCompatibilityResponse } from '@rover/rsdk/src/apiClient/latest/model/petCompatibilityResponse';
import type { PetCompatibilityResponseFilters } from '@rover/rsdk/src/apiClient/latest/model/petCompatibilityResponseFilters';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import { writePetCompatibilitySignal } from '@rover/shared/js/search/utilities/petCompatibilitySignal';

export type AnsweredQuestion = { question: Question; answer: Answer };

type PetCompatibilityAnswers = {
  questions: Question[];
  answered: AnsweredQuestion[];
  currentQuestion: Question | undefined;
  filters: PetCompatibilityResponseFilters;
  hasSavedToPets: boolean;
  pendingAnswer: Answer | undefined;
  hasSaveError: boolean;
  answer: (question: Question, answer: Answer) => void;
};

const usePetCompatibilityAnswers = (profileQuestions: Question[]): PetCompatibilityAnswers => {
  // A profile refetch drops answered questions, which would shift the queue under the owner.
  const [questions] = useState(profileQuestions);
  const [answered, setAnswered] = useState<AnsweredQuestion[]>([]);
  const [hasSavedToPets, setHasSavedToPets] = useState(false);
  const [filters, setFilters] = useState<PetCompatibilityResponseFilters>({});
  const [pendingAnswer, setPendingAnswer] = useState<Answer>();
  const [hasSaveError, setHasSaveError] = useState(false);
  const noPetNamesRef = useRef<string[]>([]);
  const savingRef = useRef(false);
  const { mutate } = useApiPetCompatibilityCreate();

  const recordSavedAnswer = (
    question: Question,
    value: Answer,
    response: PetCompatibilityResponse
  ): void => {
    const nextFilters = { ...filters, ...response.filters };
    setFilters(nextFilters);
    setAnswered((previous) => [...previous, { question, answer: value }]);
    setHasSaveError(false);
    if (response.answers.some(({ savedToCache, petNames }) => !savedToCache && petNames.length)) {
      setHasSavedToPets(true);
    }

    const noAnswers = response.answers.filter(
      ({ answer }) => answer === PetCompatibilityAnswerEnum.no
    );
    if (noAnswers.length === 0) return;
    noPetNamesRef.current = [
      ...new Set([...noPetNamesRef.current, ...noAnswers.flatMap(({ petNames: names }) => names)]),
    ];
    writePetCompatibilitySignal({
      filters: nextFilters,
      petNames: noPetNamesRef.current,
      timestamp: Date.now(),
    });
  };

  // The queue waits on each save so "Search now" always has every No's filters, and so a
  // failed answer stays on screen to retry.
  const answer = (question: Question, value: Answer): void => {
    if (savingRef.current) return;
    savingRef.current = true;
    setHasSaveError(false);
    setPendingAnswer(value);
    mutate(
      { data: { [question]: value } },
      {
        onSuccess: (response) => recordSavedAnswer(question, value, response),
        onError: (error) => {
          Logger.info('Pet compatibility answer failed to save', {
            errorMessage: (error as Error).message,
          });
          setHasSaveError(true);
        },
        onSettled: () => {
          savingRef.current = false;
          setPendingAnswer(undefined);
        },
      }
    );
  };

  return {
    questions,
    answered,
    currentQuestion: questions[answered.length],
    filters,
    hasSavedToPets,
    pendingAnswer,
    hasSaveError,
    answer,
  };
};

export default usePetCompatibilityAnswers;
